import React from 'react';
import { IntlShape } from 'react-intl';
import { Input } from 'tea-component';
import Tag from '@sg/components/Tag';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import { renderRichText } from '@sg/utils/commonPageMethod';
import DeleteButton from '@sg/components/DeleteButton';
import TagSelectComponent from '../../components/TagSelectComponent';
import TimeSelectComponent from '../../components/TimeSelectComponent';
import ProjectSelectComponent from '../../components/ProjectSelectComponent';
import InputTextComponent from '../../components/InputTextComponent';
import '../../pageConfig.scss';

// Decision Point List table
export const pointsColums = (formatMessage: IntlShape['formatMessage']) => [
  {
    header: formatMessage({ id: 'dbs_project' }),
    dataIndex: 'Project',
    key: 'Project',
    width: 160,
    render: (cvm: any) => <>
      <div className='dbs-project'>
        <div className='dbs-project-img'>
          <img src={cvm.cover || GameDefault} alt="" />
        </div>
        <div className='dbs-project-content'>
          <div className='dbs-project-title'>{cvm.name}</div>
          <div className='dbs-project-type'>
            {cvm.status && <Tag text={cvm.status} textColor='#7F6FF1' backgroundColor='rgba(127,111,241,0.12)'></Tag>}
          </div>
        </div>
      </div>
    </>,
  },
  {
    header: formatMessage({ id: 'dbs_table_urgency' }),
    dataIndex: 'urgency',
    key: 'urgency',
    width: 80,
    render: (cvm: any) => (
      cvm?.urgency ? <Tag text={cvm.urgency} type={cvm.urgency === 'Yes' ? 'warn' : 'default'}></Tag> : null
    ),
  },
  {
    header: formatMessage({ id: 'dbs_table_issue' }),
    dataIndex: 'issue',
    key: 'issue',
    width: 300,
    render: (cvm: any) => <div>{renderRichText(cvm?.issue, 'dbs-plain-text-content-wrapper')}</div>,
  },
  {
    header: formatMessage({ id: 'dbs_table_expected_time' }),
    dataIndex: 'expected_time',
    key: 'expected_time',
    width: 120,
  },
  {
    header: formatMessage({ id: 'dbs_table_bos_recommendation' }),
    dataIndex: 'bos',
    key: 'bos',
    width: 300,
    render: (cvm: any) => <div>{renderRichText(cvm?.bos, 'dbs-plain-text-content-wrapper')}</div>,
  },
];

export const editPointsColums = (
  formatMessage: IntlShape['formatMessage'],
  onDeleteRow?: (record: any) => void,
  startDate?: string,
  endDate?: string,
) => [
  {
    header: formatMessage({ id: 'dbs_project' }),
    dataIndex: 'game_id',
    key: 'game_id',
    width: 300,
    render: (record: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any[], stableUpdater: any, setEditData: any, validationErrors: Record<string, string[]>) => {
      const recordId = String(record.id);
      const recordErrors = validationErrors[recordId] || [];
      const hasNameError = recordErrors.includes('name') || recordErrors.includes('game_id');
      const hasStatusError = recordErrors.includes('status');

      return (
        <div className='dbs-project' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className='dbs-project-img'>
            <img src={record.cover || GameDefault} alt="" />
          </div>
          <div className='dbs-project-content' style={{ flex: 1 }}>
            {/* 项目名称下拉框 */}
            <div className='dbs-project-title' style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ProjectSelectComponent
                value={record.name}
                record={record}
                editData={editData}
                stableUpdater={stableUpdater}
                hasError={hasNameError}
                startDate={startDate}
                endDate={endDate}
              />
              {/* 删除按钮 */}
              <DeleteButton
                onClick={() => {
                  onDeleteRow?.(record);
                }}
                title="Delete Row"
              />
            </div>
            {/* 状态标签 */}
            <div className='dbs-project-type'>
              <TagSelectComponent
                value={record.status}
                record={record}
                editData={editData}
                stableUpdater={stableUpdater}
                fieldKey="status"
                options={[
                  { value: 'Live', text: 'Live' },
                  { value: 'To Be Launched', text: 'To Be Launched' },
                  { value: 'Wishlist', text: 'Wishlist' },
                  { value: 'Studio Support', text: 'Studio Support' },
                  { value: 'Termination', text: 'Termination' },
                ]}
                colorType="fixed"
                disabled={(() => {
                  const isNewRow = record.id && String(record.id).startsWith('new_');
                  const hasSelectedProject = record.name || record.game_id;
                  return isNewRow && !hasSelectedProject;
                })()}
                hasError={hasStatusError}
              />
            </div>
          </div>
        </div>
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_table_urgency' }),
    dataIndex: 'urgency',
    key: 'urgency',
    width: 150,
    render: (record: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any[], stableUpdater: any, setEditData: any, validationErrors: Record<string, string[]>) => {
      const recordId = String(record.id);
      const recordErrors = validationErrors[recordId] || [];
      const hasUrgencyError = recordErrors.includes('urgency');

      return (
        <TagSelectComponent
          value={record.urgency}
          record={record}
          editData={editData}
          stableUpdater={stableUpdater}
          fieldKey="urgency"
          options={[
            { value: 'Yes', text: 'Yes' },
            { value: 'No', text: 'No' },
          ]}
          colorType="dynamic"
          disabled={(() => {
            const isNewRow = record.id && String(record.id).startsWith('new_');
            const hasSelectedProject = record.name || record.game_id;
            return isNewRow && !hasSelectedProject;
          })()}
          hasError={hasUrgencyError}
        />
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_table_issue' }),
    dataIndex: 'issue',
    key: 'issue',
    width: 300,
    render: (record: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any[], stableUpdater: any) => {
      // 判断是否是新增行且未选择项目
      const isNewRow = record.id && String(record.id).startsWith('new_');
      const hasSelectedProject = record.name || record.game_id;
      const isDisabled = isNewRow && !hasSelectedProject;

      if (isDisabled) {
        return <Input disabled placeholder="Please select project first" style={{ width: '100%' }} />;
      }

      return (
        <InputTextComponent
          value={record.issue}
          record={record}
          editData={editData}
          stableUpdater={stableUpdater}
          fieldKey="issue"
        />
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_table_expected_time' }),
    dataIndex: 'expected_time',
    key: 'expected_time',
    width: 180,
    render: (record: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any[], stableUpdater: any, setEditData: any, validationErrors: Record<string, string[]>) => {
      const recordId = String(record.id);
      const recordErrors = validationErrors[recordId] || [];
      const hasExpectedTimeError = recordErrors.includes('expected_time');

      return (
        <TimeSelectComponent
          value={record.expected_time}
          record={record}
          editData={editData}
          stableUpdater={stableUpdater}
          disabled={(() => {
            const isNewRow = record.id && String(record.id).startsWith('new_');
            const hasSelectedProject = record.name || record.game_id;
            return isNewRow && !hasSelectedProject;
          })()}
          hasError={hasExpectedTimeError}
        />
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_table_bos_recommendation' }),
    dataIndex: 'bos',
    key: 'bos',
    width: 300,
    render: (record: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any[], stableUpdater: any) => {
      // 判断是否是新增行且未选择项目
      const isNewRow = record.id && String(record.id).startsWith('new_');
      const hasSelectedProject = record.name || record.game_id;
      const isDisabled = isNewRow && !hasSelectedProject;

      if (isDisabled) {
        return <Input disabled placeholder="Please select project first" style={{ width: '100%' }} />;
      }

      return (
        <InputTextComponent
          value={record.bos}
          record={record}
          editData={editData}
          stableUpdater={stableUpdater}
          fieldKey="bos"
        />
      );
    },
  },
];
